import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_ADDRESS_TYPE, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, BusinessUnitAddress, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { BusinessUnitAddressService, BusinessUnitService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class BusinessUnitAddressItemComponentBase extends CrudItemComponent<BusinessUnitAddress, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];

  addrTypeSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected businessUnitAddressService: BusinessUnitAddressService,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(BUSINESS_UNIT_ADDRESS_TYPE, fb, elementRef, router, route, userStorage, dialog, businessUnitAddressService, injector, snackbar);
  }

  createNewItem() {
    return new BusinessUnitAddress();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      businessUnitAddressId: [null, [requiredValidator,]],
      businessUnit: [null, []],
      addr1: [null, [requiredValidator,]],
      addr2: [null, []],
      addr3: [null, []],
      addrType: [null, []],
      county: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      description: [null, []],
      lockVersion: [null, []],
      postcode: [null, [requiredValidator,]],
      town: [null, []],
      updatedBy: [null, []],
      updatedDate: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.businessUnitAddressId?.disable();
    }
  }
  
  businessUnitAddressIdChanging(value: number, unsetDependents: boolean = true) {}
  
  businessUnitAddressIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitAddressIdChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitAddressIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  addr1Changing(value: string, unsetDependents: boolean = true) {}
  
  addr1Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr1ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr1ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr2Changing(value: string, unsetDependents: boolean = true) {}
  
  addr2Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr2ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr2ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr3Changing(value: string, unsetDependents: boolean = true) {}
  
  addr3Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr3ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr3ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addrTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('BU_ADDR_TYPE').subscribe(
      data => {
        this.addrTypeSelectionList = data;
      }
    );
  }

  addrTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  addrTypeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addrTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  addrTypeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  countyChanging(value: string, unsetDependents: boolean = true) {}
  
  countyChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.countyChangedByUser(value, unsetDependents);
    }
  }  
  
  countyChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  descriptionChanging(value: string, unsetDependents: boolean = true) {}
  
  descriptionChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.descriptionChangedByUser(value, unsetDependents);
    }
  }  
  
  descriptionChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  postcodeChanging(value: string, unsetDependents: boolean = true) {}
  
  postcodeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.postcodeChangedByUser(value, unsetDependents);
    }
  }  
  
  postcodeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  townChanging(value: string, unsetDependents: boolean = true) {}
  
  townChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.townChangedByUser(value, unsetDependents);
    }
  }  
  
  townChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get businessUnitAddressId() {
    return this.control('businessUnitAddressId');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get addr1() {
    return this.control('addr1');
  }
  
  get addr2() {
    return this.control('addr2');
  }
  
  get addr3() {
    return this.control('addr3');
  }
  
  get addrType() {
    return this.control('addrType');
  }
  
  get county() {
    return this.control('county');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get description() {
    return this.control('description');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get postcode() {
    return this.control('postcode');
  }
  
  get town() {
    return this.control('town');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
}





